import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { useTranslation } from 'react-i18next';
import {
  fetchTemplateById,
  updateExistingTemplate,
  clearActiveTemplate,
  Template,
  TemplateSection,
} from '../../../../redux/features/doctor/Dashboard/templates/templates';
import { v4 as uuidv4 } from 'uuid';
import { Box, Typography, Button, CircularProgress, Paper, TextField, IconButton, Alert, Divider, useMediaQuery } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditorSection from './components/EditorSection';
import TemplatePreview from './components/TemplatePreview';

const TemplateEditor: React.FC = () => {
  const { t } = useTranslation();
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { activeTemplate, status } = useAppSelector((state) => state.templates);
  const colors = useAppSelector((state) => state.theme.colors);

  // FIX: Add media query for mobile responsiveness
  const isMobile = useMediaQuery('(max-width: 768px)');

  const [editedTemplate, setEditedTemplate] = useState<Template | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (templateId) {
      dispatch(fetchTemplateById(templateId));
    }
    return () => {
      dispatch(clearActiveTemplate());
    };
  }, [templateId, dispatch]);

  useEffect(() => {
    if (activeTemplate) {
      const templateWithSectionIds = {
        ...activeTemplate,
        sections: activeTemplate.sections.map(section => ({ ...section, id: uuidv4() }))
      };
      setEditedTemplate(templateWithSectionIds);
    }
  }, [activeTemplate]);

  const handleSectionUpdate = useCallback((sectionIndex: number, updatedSection: TemplateSection) => {
    setEditedTemplate(prev => {
      if (!prev) return null;
      const newSections = [...prev.sections];
      newSections[sectionIndex] = updatedSection;
      return { ...prev, sections: newSections };
    });
  }, []);

  const handleSectionDelete = useCallback((sectionIndex: number) => {
    setEditedTemplate(prev => {
      if (!prev) return null;
      const newSections = prev.sections.filter((_, index) => index !== sectionIndex);
      return { ...prev, sections: newSections };
    });
  }, []);

  const handleAddSection = () => {
    const newSection: TemplateSection = {
      id: uuidv4(),
      title: 'New Section',
      description: 'Instructions for this section.',
      exampleText: 'Example content.',
    };
    setEditedTemplate(prev => prev ? { ...prev, sections: [...prev.sections, newSection] } : null);
  };

  const handleSave = async () => {
    if (!editedTemplate) return;
    setIsSaving(true);
    const templateToSave = {
      ...editedTemplate,
      sections: editedTemplate.sections.map(({ id, ...rest }) => rest)
    };
    await dispatch(updateExistingTemplate(templateToSave as Template));
    setIsSaving(false);
  };
  
  const handleHeaderChange = (field: 'title' | 'category', value: string) => {
    setEditedTemplate(prev => prev ? { ...prev, [field]: value } : null);
  };

  const inputStyles = {
    '& .MuiInputBase-input': { color: colors?.text },
    '& .MuiInputLabel-root': { color: colors?.listInfo },
    '& .MuiOutlinedInput-root': {
      backgroundColor: colors?.interface,
      '& fieldset': { borderColor: colors?.folderDataRowBorderColor },
      '&:hover fieldset': { borderColor: colors?.lightBlue },
      '&.Mui-focused fieldset': { borderColor: colors?.lightBlue, borderWidth: '2px' },
    },
  };

  if (status === 'loading' && !editedTemplate) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress sx={{ color: colors?.lightBlue }}/></Box>;
  }

  if (!editedTemplate) {
    return <Alert severity="warning" sx={{ m: 2 }}>Template not found or still loading...</Alert>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, px: isMobile ? 0 : 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
          <IconButton onClick={() => navigate('/dashboard/templates')} sx={{ color: colors?.text, '&:hover': { backgroundColor: colors?.sectionHover } }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography noWrap variant={isMobile ? 'h6' : 'h5'} sx={{ color: colors?.text, fontWeight: 'bold' }}>
            {t('editing')}: <span style={{ color: colors?.lightBlue }}>{editedTemplate.title}</span>
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={isSaving || status === 'loading'}
          sx={{ 
            backgroundColor: colors?.lightBlue, 
            '&:hover': { backgroundColor: colors?.darkBlue }, 
            fontWeight: 'bold', 
            borderRadius: 2, 
            px: isMobile ? 2 : 3, 
            py: isMobile ? 1 : 1.5,
            ml: 1
          }}
        >
          {isMobile ? t('save') : t('saveTemplate')}
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexGrow: 1, gap: 3, overflow: 'hidden', flexDirection: isMobile ? 'column' : 'row' }}>
        <Paper
          elevation={0}
          sx={{
            width: isMobile ? '100%' : '50%',
            p: isMobile ? 1.5 : 2,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            backgroundColor: colors?.container,
            borderRadius: 4,
            border: `1px solid ${colors?.folderDataRowBorderColor}`
          }}
        >
          <Typography variant="h6" sx={{ color: colors?.text, px: 1 }}>{t('builder')}</Typography>
          <TextField label={t('templateTitle')} value={editedTemplate.title} onChange={(e) => handleHeaderChange('title', e.target.value)} fullWidth variant="outlined" sx={inputStyles} />
          <TextField label={t('category')} value={editedTemplate.category} onChange={(e) => handleHeaderChange('category', e.target.value)} fullWidth variant="outlined" sx={inputStyles} />
          
          <Divider sx={{ borderColor: colors?.folderDataRowBorderColor, my: 1 }} />

          {editedTemplate.sections.map((section, index) => (
            <EditorSection
              key={section.id}
              section={section}
              onUpdate={(updated) => handleSectionUpdate(index, updated)}
              onDelete={() => handleSectionDelete(index)}
            />
          ))}
          <Button 
            variant="contained" 
            startIcon={<AddCircleOutlineIcon />} 
            onClick={handleAddSection} 
            sx={{ 
              mt: 2, 
              alignSelf: 'center',
              backgroundColor: colors?.lightBlue,
              '&:hover': { 
                backgroundColor: colors?.darkBlue
              },
              fontWeight: 'bold',
              borderRadius: 2, 
              py: 1,
              px: 3,
            }}
          >
            {t('addASection')}
          </Button>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            width: isMobile ? '100%' : '50%',
            p: isMobile ? 2 : 3,
            overflowY: 'auto',
            backgroundColor: colors?.container,
            borderRadius: 4,
            border: `1px solid ${colors?.folderDataRowBorderColor}`
          }}
        >
          <TemplatePreview template={editedTemplate} />
        </Paper>
      </Box>
    </Box>
  );
};

export default TemplateEditor;